import React, { useState } from 'react';
import {
  Box,
  Container,
  Typography,
  Grid,
  Card,
  CardContent,
  List,
  ListItem,
  ListItemIcon,
  ListItemText,
  Tabs,
  Tab,
} from '@mui/material';
import { motion } from 'framer-motion';
import { useIntl } from 'react-intl';
import CheckCircleIcon from '@mui/icons-material/CheckCircle';
import BusinessIcon from '@mui/icons-material/Business';
import SchoolIcon from '@mui/icons-material/School';
import FlightIcon from '@mui/icons-material/Flight';
import FamilyRestroomIcon from '@mui/icons-material/FamilyRestroom';
import WorkIcon from '@mui/icons-material/Work';
import useScrollToTop from '../../hooks/useScrollToTop';

interface TabPanelProps {
  children?: React.ReactNode;
  index: number;
  value: number;
}

const TabPanel = (props: TabPanelProps) => {
  const { children, value, index, ...other } = props;

  return (
    <div
      role="tabpanel"
      hidden={value !== index}
      id={`newZealand-tabpanel-${index}`}
      aria-labelledby={`newZealand-tab-${index}`}
      {...other}
    >
      {value === index && <Box sx={{ py: 3 }}>{children}</Box>}
    </div>
  );
};

const NewZealand: React.FC = () => {
  const intl = useIntl();
  const t = (id: string) => intl.formatMessage({ id });
  const [selectedTab, setSelectedTab] = useState(0);
  useScrollToTop();

  const handleTabChange = (event: React.SyntheticEvent, newValue: number) => {
    setSelectedTab(newValue);
  };

  const growthBenefits = [
    { icon: <FlightIcon />, text: t('citizenship.newZealand.growth.benefits.travel') },
    { icon: <WorkIcon />, text: t('citizenship.newZealand.growth.benefits.work') },
    { icon: <SchoolIcon />, text: t('citizenship.newZealand.growth.benefits.education') },
    { icon: <BusinessIcon />, text: t('citizenship.newZealand.growth.benefits.business') },
    { icon: <FamilyRestroomIcon />, text: t('citizenship.newZealand.growth.benefits.family') },
    { icon: <CheckCircleIcon />, text: t('citizenship.newZealand.growth.benefits.quality') },
  ];

  const balancedBenefits = [
    { icon: <FlightIcon />, text: t('citizenship.newZealand.balanced.benefits.travel') },
    { icon: <WorkIcon />, text: t('citizenship.newZealand.balanced.benefits.work') },
    { icon: <SchoolIcon />, text: t('citizenship.newZealand.balanced.benefits.education') },
    { icon: <BusinessIcon />, text: t('citizenship.newZealand.balanced.benefits.business') },
    { icon: <FamilyRestroomIcon />, text: t('citizenship.newZealand.balanced.benefits.family') },
    { icon: <CheckCircleIcon />, text: t('citizenship.newZealand.balanced.benefits.quality') },
  ];

  const growthRequirements = [
    t('citizenship.newZealand.growth.requirements.investment'),
    t('citizenship.newZealand.growth.requirements.approval'),
    t('citizenship.newZealand.growth.requirements.timeline'),
    t('citizenship.newZealand.growth.requirements.cash'),
    t('citizenship.newZealand.growth.requirements.fees'),
    t('citizenship.newZealand.growth.requirements.filing'),
  ];

  const balancedRequirements = [
    t('citizenship.newZealand.balanced.requirements.investment'),
    t('citizenship.newZealand.balanced.requirements.approval'),
    t('citizenship.newZealand.balanced.requirements.timeline'),
    t('citizenship.newZealand.balanced.requirements.cash'),
    t('citizenship.newZealand.balanced.requirements.fees'),
    t('citizenship.newZealand.balanced.requirements.filing'),
  ];

  return (
    <Box>
      {/* Hero Section */}
      <Box
        sx={{
          background: 'linear-gradient(rgba(0, 0, 0, 0.5), rgba(0, 0, 0, 0.5))',
          backgroundColor: '#2e7d32',
          backgroundSize: 'cover',
          backgroundPosition: 'center',
          height: '60vh',
          display: 'flex',
          alignItems: 'center',
          justifyContent: 'center',
          color: 'white',
          textAlign: 'center',
        }}
      >
        <Container>
          <motion.div
            initial={{ opacity: 0, y: 20 }}
            animate={{ opacity: 1, y: 0 }}
            transition={{ duration: 0.8 }}
          >
            <Typography variant="h2" component="h1" gutterBottom>
              {t('citizenship.newZealand.hero.title')}
            </Typography>
            <Typography variant="h5" gutterBottom>
              {t('citizenship.newZealand.hero.subtitle')}
            </Typography>
          </motion.div>
        </Container>
      </Box>

      {/* Program Tabs */}
      <Container sx={{ py: 4 }}>
        <Box sx={{ borderBottom: 1, borderColor: 'divider' }}>
          <Tabs
            value={selectedTab}
            onChange={handleTabChange}
            centered
            sx={{
              '& .MuiTab-root': {
                fontSize: '1.1rem',
                fontWeight: 'medium',
                minWidth: 200,
              },
            }}
          >
            <Tab label={t('citizenship.newZealand.tabs.growth')} />
            <Tab label={t('citizenship.newZealand.tabs.balanced')} />
          </Tabs>
        </Box>
      </Container>

      {/* Growth Program */}
      <TabPanel value={selectedTab} index={0}>
        <Container sx={{ py: 8 }}>
          <Grid container spacing={4} justifyContent="center">
            <Grid item xs={12} md={8}>
              <motion.div
                initial={{ opacity: 0, y: 20 }}
                animate={{ opacity: 1, y: 0 }}
                transition={{ duration: 0.8 }}
              >
                <Typography variant="h4" gutterBottom align="center">
                  {t('citizenship.newZealand.growth.overview.title')}
                </Typography>
                <Typography variant="body1" paragraph align="center">
                  {t('citizenship.newZealand.growth.overview.description1')}
                </Typography>
                <Typography variant="body1" paragraph align="center">
                  {t('citizenship.newZealand.growth.overview.description2')}
                </Typography>
              </motion.div>
            </Grid>
          </Grid>
        </Container>

        {/* Growth Benefits */}
        <Container sx={{ py: 8 }}>
          <Grid container spacing={4} justifyContent="center">
            <Grid item xs={12}>
              <motion.div
                initial={{ opacity: 0, y: 20 }}
                animate={{ opacity: 1, y: 0 }}
                transition={{ duration: 0.8 }}
              >
                <Typography variant="h4" gutterBottom align="center">
                  {t('citizenship.newZealand.growth.benefits.title')}
                </Typography>
                <Grid container spacing={4} sx={{ mt: 4 }} justifyContent="center">
                  {growthBenefits.map((benefit, index) => (
                    <Grid item xs={12} sm={6} md={4} key={index}>
                      <motion.div
                        initial={{ opacity: 0, y: 20 }}
                        whileInView={{ opacity: 1, y: 0 }}
                        transition={{ duration: 0.5, delay: index * 0.1 }}
                        viewport={{ once: true }}
                      >
                        <Card sx={{ height: '100%' }}>
                          <CardContent>
                            <Box sx={{ display: 'flex', alignItems: 'center', mb: 2 }}>
                              <Box sx={{ color: 'primary.main', mr: 2 }}>
                                {benefit.icon}
                              </Box>
                              <Typography variant="h6" component="h3">
                                {benefit.text}
                              </Typography>
                            </Box>
                          </CardContent>
                        </Card>
                      </motion.div>
                    </Grid>
                  ))}
                </Grid>
              </motion.div>
            </Grid>
          </Grid>
        </Container>

        {/* Growth Requirements */}
        <Container sx={{ py: 8 }}>
          <Typography variant="h4" component="h2" align="center" gutterBottom>
            {t('citizenship.newZealand.growth.requirements.title')}
          </Typography>
          <Grid container spacing={4} sx={{ mt: 4 }}>
            <Grid item xs={12} md={6}>
              <motion.div
                initial={{ opacity: 0, x: -20 }}
                whileInView={{ opacity: 1, x: 0 }}
                transition={{ duration: 0.8 }}
                viewport={{ once: true }}
              >
                <Card sx={{ height: '100%' }}>
                  <CardContent>
                    <Typography variant="h5" gutterBottom>
                      {t('citizenship.newZealand.growth.requirements.investmentTitle')}
                    </Typography>
                    <List>
                      {growthRequirements.map((requirement, index) => (
                        <ListItem key={index}>
                          <ListItemIcon>
                            <CheckCircleIcon color="primary" />
                          </ListItemIcon>
                          <ListItemText primary={requirement} />
                        </ListItem>
                      ))}
                    </List>
                  </CardContent>
                </Card>
              </motion.div>
            </Grid>
            <Grid item xs={12} md={6}>
              <motion.div
                initial={{ opacity: 0, x: 20 }}
                whileInView={{ opacity: 1, x: 0 }}
                transition={{ duration: 0.8 }}
                viewport={{ once: true }}
              >
                <Card sx={{ height: '100%' }}>
                  <CardContent>
                    <Typography variant="h6" gutterBottom>
                      {t('citizenship.newZealand.growth.timeline.title')}
                    </Typography>
                    <List>
                      <ListItem>
                        <ListItemText
                          primary={t('citizenship.newZealand.growth.timeline.initial.title')}
                          secondary={t('citizenship.newZealand.growth.timeline.initial.description')}
                        />
                      </ListItem>
                      <ListItem>
                        <ListItemText
                          primary={t('citizenship.newZealand.growth.timeline.investment.title')}
                          secondary={t('citizenship.newZealand.growth.timeline.investment.description')}
                        />
                      </ListItem>
                      <ListItem>
                        <ListItemText
                          primary={t('citizenship.newZealand.growth.timeline.residence.title')}
                          secondary={t('citizenship.newZealand.growth.timeline.residence.description')}
                        />
                      </ListItem>
                      <ListItem>
                        <ListItemText
                          primary={t('citizenship.newZealand.growth.timeline.permanent.title')}
                          secondary={t('citizenship.newZealand.growth.timeline.permanent.description')}
                        />
                      </ListItem>
                    </List>
                  </CardContent>
                </Card>
              </motion.div>
            </Grid>
          </Grid>
        </Container>
      </TabPanel>

      {/* Balanced Program */}
      <TabPanel value={selectedTab} index={1}>
        <Container sx={{ py: 8 }}>
          <Grid container spacing={4} justifyContent="center">
            <Grid item xs={12} md={8}>
              <motion.div
                initial={{ opacity: 0, y: 20 }}
                animate={{ opacity: 1, y: 0 }}
                transition={{ duration: 0.8 }}
              >
                <Typography variant="h4" gutterBottom align="center">
                  {t('citizenship.newZealand.balanced.overview.title')}
                </Typography>
                <Typography variant="body1" paragraph align="center">
                  {t('citizenship.newZealand.balanced.overview.description1')}
                </Typography>
                <Typography variant="body1" paragraph align="center">
                  {t('citizenship.newZealand.balanced.overview.description2')}
                </Typography>
              </motion.div>
            </Grid>
          </Grid>
        </Container>

        {/* Balanced Benefits */}
        <Container sx={{ py: 8 }}>
          <Grid container spacing={4} justifyContent="center">
            <Grid item xs={12}>
              <motion.div
                initial={{ opacity: 0, y: 20 }}
                animate={{ opacity: 1, y: 0 }}
                transition={{ duration: 0.8 }}
              >
                <Typography variant="h4" gutterBottom align="center">
                  {t('citizenship.newZealand.balanced.benefits.title')}
                </Typography>
                <Grid container spacing={4} sx={{ mt: 4 }} justifyContent="center">
                  {balancedBenefits.map((benefit, index) => (
                    <Grid item xs={12} sm={6} md={4} key={index}>
                      <motion.div
                        initial={{ opacity: 0, y: 20 }}
                        whileInView={{ opacity: 1, y: 0 }}
                        transition={{ duration: 0.5, delay: index * 0.1 }}
                        viewport={{ once: true }}
                      >
                        <Card sx={{ height: '100%' }}>
                          <CardContent>
                            <Box sx={{ display: 'flex', alignItems: 'center', mb: 2 }}>
                              <Box sx={{ color: 'primary.main', mr: 2 }}>
                                {benefit.icon}
                              </Box>
                              <Typography variant="h6" component="h3">
                                {benefit.text}
                              </Typography>
                            </Box>
                          </CardContent>
                        </Card>
                      </motion.div>
                    </Grid>
                  ))}
                </Grid>
              </motion.div>
            </Grid>
          </Grid>
        </Container>

        {/* Balanced Requirements */}
        <Container sx={{ py: 8 }}>
          <Typography variant="h4" component="h2" align="center" gutterBottom>
            {t('citizenship.newZealand.balanced.requirements.title')}
          </Typography>
          <Grid container spacing={4} sx={{ mt: 4 }}>
            <Grid item xs={12} md={6}>
              <motion.div
                initial={{ opacity: 0, x: -20 }}
                whileInView={{ opacity: 1, x: 0 }}
                transition={{ duration: 0.8 }}
                viewport={{ once: true }}
              >
                <Card sx={{ height: '100%' }}>
                  <CardContent>
                    <Typography variant="h5" gutterBottom>
                      {t('citizenship.newZealand.balanced.requirements.title')}
                    </Typography>
                    <List>
                      {balancedRequirements.map((requirement, index) => (
                        <ListItem key={index}>
                          <ListItemIcon>
                            <CheckCircleIcon color="primary" />
                          </ListItemIcon>
                          <ListItemText primary={requirement} />
                        </ListItem>
                      ))}
                    </List>
                  </CardContent>
                </Card>
              </motion.div>
            </Grid>
            <Grid item xs={12} md={6}>
              <motion.div
                initial={{ opacity: 0, x: 20 }}
                whileInView={{ opacity: 1, x: 0 }}
                transition={{ duration: 0.8 }}
                viewport={{ once: true }}
              >
                <Card sx={{ height: '100%' }}>
                  <CardContent>
                    <Typography variant="h6" gutterBottom>
                      {t('citizenship.newZealand.balanced.timeline.title')}
                    </Typography>
                    <List>
                      <ListItem>
                        <ListItemText
                          primary={t('citizenship.newZealand.balanced.timeline.initial.title')}
                          secondary={t('citizenship.newZealand.balanced.timeline.initial.description')}
                        />
                      </ListItem>
                      <ListItem>
                        <ListItemText
                          primary={t('citizenship.newZealand.balanced.timeline.investment.title')}
                          secondary={t('citizenship.newZealand.balanced.timeline.investment.description')}
                        />
                      </ListItem>
                      <ListItem>
                        <ListItemText
                          primary={t('citizenship.newZealand.balanced.timeline.residence.title')}
                          secondary={t('citizenship.newZealand.balanced.timeline.residence.description')}
                        />
                      </ListItem>
                      <ListItem>
                        <ListItemText
                          primary={t('citizenship.newZealand.balanced.timeline.permanent.title')}
                          secondary={t('citizenship.newZealand.balanced.timeline.permanent.description')}
                        />
                      </ListItem>
                    </List>
                  </CardContent>
                </Card>
              </motion.div>
            </Grid>
          </Grid>
        </Container>
      </TabPanel>
    </Box>
  );
};

export default NewZealand;
